import * as types from '../constants/actionTypes';
import initialState from './initialState';
import Immutable, { ImmutableObject } from 'seamless-immutable';
import { IMAGE_ACTION_TYPE, PLACE_ID_DEFAULT } from '../constants/constants';
import User from 'redhotpie-shared-lib/models/user';
import { IMainPhoto } from 'redhotpie-shared-lib/interfaces/IUser';
import { IUserLoginInfo } from '../interfaces/IUser';

export interface IFilterActiveState {
  minAge: number;
  maxAge: number;
  isMatch: boolean;
  genders: any[];
  location: any;
  disableProximity: boolean;
  searchRadius: number;
  seekingType?: number;
}

export interface IUserReducerState {
  isWaitingAvatarLoading: boolean;
  isWaitingGetMainPhoto: boolean;
  data: User[];
  userIdsCalledAPI: number[];
  users: User[];
  usersBlockMe: number[];
  usersBlocked: IUserLoginInfo[];
  isWhisperChatRooms: boolean;
  filterActiveModalState: IFilterActiveState;
  filterActiveStateModalDefault: IFilterActiveState;
  usersActive: any[];
  usersActiveByFiltered: any[];
  loadingUsersActive: boolean;
  loadingUsersActiveByFiltered: boolean;
  hasFilteredUsersActive: boolean;
  timeGetRequestActiveAPI: number;
  timeGetRequestFilterActiveAPI: number;
  sizeParamGetUserActive: number;
  activeUserPopularState: any[];
  activeUserPopularStateSubUrb: any[];
  currentStateSelect: string | null;
  timeInBackground: number | null;
  userRemainingInvitesCredit: any;
}

interface IAction {
  type: string;
  data: any;
  userId: number;
  userIds: number[];
  members: User[];
  forceLoadAvatar?: boolean;
  googleMapPlaceID: string;
}

export default function usersReducer(state: ImmutableObject<IUserReducerState> = initialState.users, action: IAction) {
  switch (action.type) {
    case types.UPDATE_USERS_STATE_SUCCESS: {
      return state.merge({ ...action.data });
    }
    case types.GET_MAIN_PHOTO_SUCCESS: {
      const mainPhoto = action.data;
      const currentData = state.data.asMutable();
      const newItem = {
        userId: mainPhoto.userId,
        avatarUrl: mainPhoto.data ? mainPhoto.data.thumbUrl : '',
        fullUrl: mainPhoto.data ? mainPhoto.data.fullUrl : '',
        imageActionType: mainPhoto.data.imageActionType || IMAGE_ACTION_TYPE.SHOW_IMAGE,
        showSafeMode: mainPhoto.data.showSafeMode || false,
      };

      return state
        .set('data', [...currentData, newItem])
        .set('isWaitingAvatarLoading', false)
        .set('isWaitingGetMainPhoto', false);
    }

    case types.GET_MAIN_PHOTOS_REQUEST:
    case types.GET_MAIN_PHOTO_REQUEST: {
      return state.set('isWaitingGetMainPhoto', true);
    }

    case types.GET_MAIN_PHOTOS_SUCCESS: {
      const mainPhoto = action.data;
      const currentData = state.data.asMutable() || [];
      const newItems = [];
      const len = mainPhoto.data.length;
      // Use a Set to track IDs we've seen in this batch + existing IDs for O(1) lookup
      const existingIds = new Set(currentData.map(u => u.userId));

      for (let i = 0; i < len; i++) {
        const targetId = Number.parseInt(mainPhoto.userId[i]);
        if (!existingIds.has(targetId)) {
          existingIds.add(targetId); // Mark as seen
          newItems.push({
            userId: targetId,
            avatarUrl: mainPhoto.data[i].thumbUrl ? mainPhoto.data[i].thumbUrl : '',
            fullUrl: mainPhoto.data[i].fullUrl ? mainPhoto.data[i].fullUrl : '',
            imageActionType: mainPhoto.data[i].imageActionType || IMAGE_ACTION_TYPE.SHOW_IMAGE,
            showSafeMode: mainPhoto.data[i].showSafeMode || false,
          });
        }
      }

      let nextState = state.set('isWaitingAvatarLoading', false).set('isWaitingGetMainPhoto', false);
      if (newItems.length > 0) {
        nextState = nextState.set('data', [...currentData, ...newItems]);
      }
      return nextState;
    }
    case types.GET_ROOM_MEMBERS_DETAIL_REQUEST: {
      const { userIds } = action;
      const userIdsCalled = state.userIdsCalledAPI || [];
      // Combine existing with new, utilizing Set to naturally deduplicate
      const uniqueIds = Array.from(new Set([...userIdsCalled.asMutable(), ...userIds]));

      if (uniqueIds.length !== userIdsCalled.length) {
        return state.set('userIdsCalledAPI', uniqueIds);
      }
      return state;
    }
    case types.GET_ROOM_MEMBERS_DETAIL: {
      const { members } = action;
      const currentUsers = state.users.asMutable() || [];
      const existingUserIds = new Set(currentUsers.map(u => u.userId));
      const newMembers: User[] = [];

      members.forEach(m => {
        if (!existingUserIds.has(m.userId)) {
          existingUserIds.add(m.userId);
          newMembers.push(m);
        }
      });

      if (newMembers.length > 0) {
        return state.set('users', [...currentUsers, ...newMembers]);
      }
      return state;
    }

    case types.UPDATE_WHISPER_STATUS_REALTIME: {
      const message = action.data;
      const currentUsers = state.users || [];

      const newUsers = currentUsers.map(u => {
        if (u.xmppId === message.from) {
          return u.set('isWhisperMessage', message.data.data);
        }
        return u;
      });

      return state.set('isWhisperChatRooms', !state.isWhisperChatRooms).set('users', newUsers);
    }
    case types.GET_PROFILE_GROUP_CHAT_SUCCESS: {
      const currentUsers = state.users.asMutable() || [];

      if (!currentUsers.some(e => e.userId === action.data.userId)) {
        state = state.set('users', [...currentUsers, action.data]);
      }

      const userIdsCalled = state.userIdsCalledAPI || [];
      if (!userIdsCalled.includes(action.data.userId)) {
        state = state.set('userIdsCalledAPI', [...userIdsCalled.asMutable(), action.data.userId]);
      }
      return state;
    }
    case types.GET_USERS_WHO_BLOCK_ME: {
      return state.set('usersBlockMe', action.data);
    }
    case types.GET_USERS_BLOCKED: {
      return state.set('usersBlocked', action.data);
    }
    case types.UPDATE_USER_REMAINING_INVITES_CREDIT: {
      return state.set('userRemainingInvitesCredit', action.data);
    }

    case types.FILTER_CONVERSATIONS: {
      const { keyword, secondaryIndex, primaryIndex, isNative } = action.data;
      if (!action.forceLoadAvatar && ((keyword === '' && secondaryIndex === 0 && primaryIndex === 0) || !isNative)) {
        return state;
      }
      return state;
    }
    case types.USER_LOGOUT_SUCCESS: {
      return initialState.users;
    }

    case types.DELETE_BLOCK_SUCCESS: {
      const usersBlocked = state.usersBlocked || [];
      return state.set(
        'usersBlocked',
        usersBlocked.filter(u => u.userId !== action.userId)
      );
    }

    case types.GET_ACTIVE_USERS_BY_FILTER_REQUEST: {
      return state.set('loadingUsersActiveByFiltered', true).set('timeGetRequestFilterActiveAPI', new Date().getTime());
    }

    case types.TOGGLE_HAS_FILTERED_USERS_ACTIVE_STATUS: {
      return state.set('hasFilteredUsersActive', action.data);
    }

    case types.GET_ACTIVE_USERS_REQUEST: {
      const { size, time } = action.data;
      return state.set('loadingUsersActive', true).set('sizeParamGetUserActive', size).set('timeGetRequestActiveAPI', time);
    }

    case types.GET_ACTIVE_USERS_FILTERED_FAILED: {
      return state.set('loadingUsersActiveByFiltered', false);
    }

    case types.GET_ACTIVE_USERS_FAILED: {
      return state
        .set('loadingUsersActive', false)
        .set('sizeParamGetUserActive', initialState.users.sizeParamGetUserActive)
        .set('usersActive', []);
    }

    case types.GET_ACTIVE_USERS_SUCCESS: {
      return state.merge({
        usersActive: action.data,
        loadingUsersActive: false,
      });
    }

    case types.GET_ACTIVE_USERS_FILTERED_SUCCESS: {
      return state.merge({
        usersActiveByFiltered: action.data,
        loadingUsersActiveByFiltered: false,
        hasFilteredUsersActive: true,
      });
    }

    case types.GET_ACTIVE_USER_POPULAR_COUNT_SUCCESS: {
      const { data, googleMapPlaceID } = action;
      if (!googleMapPlaceID) {
        const activeUserPopularState = [
          {
            count: (data[0] && data[0].count) || 0,
            googleMapPlaceID: PLACE_ID_DEFAULT,
            placesParentID: null,
            text: 'Australia',
            textAbbr: 'Australia',
            value: 1,
          },
        ];
        return state.set('activeUserPopularState', activeUserPopularState).set(
          'activeUserPopularStateSubUrb',
          data.map((i: any) => ({ ...i, placesParentID: PLACE_ID_DEFAULT }))
        );
      } else {
        return state.merge({
          activeUserPopularStateSubUrb: data,
          currentStateSelect: googleMapPlaceID,
        });
      }
    }

    default:
      return state;
  }
}
